import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Home from "./pages/Home";
import Submit from "./pages/Submit";
import Claim from "./pages/Claim";
import Vendor from "./pages/Vendor";
import ForVendors from "./pages/ForVendors";
import PaymentResult from "./pages/PaymentResult";
import AdminLogin from "./pages/AdminLogin";
import Admin from "./pages/Admin";
import AdminInbox from "./pages/AdminInbox";
import Contact from "./pages/Contact";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ToolDetail from "./pages/ToolDetail";
import Stack from "./pages/Stack";
import Terminal from "./pages/Terminal";
import Sitemap from "./pages/Sitemap";
import TickerIndex from "./pages/TickerIndex";
import Build from "./pages/Build";
import Alternatives from "./pages/Alternatives";
import Compare from "./pages/Compare";
import ListView from "./pages/ListView";
import AuthCallback from "./pages/AuthCallback";
import Install from "./pages/Install";
import Badge from "./pages/Badge";
import Best from "./pages/Best";
import BestIndex from "./pages/BestIndex";
import CommandPalette from "./components/CommandPalette";
import MyStackBar from "./components/MyStackBar";
import WorkspaceLauncher from "./components/WorkspaceLauncher";
import Workspace from "./pages/Workspace";
import { track } from "./lib/track";

// Fire a `pageview` event on every route change (anonymous, first-party).
function RouteTracker() {
  const loc = useLocation();
  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) return; // don't track admin usage
    track("pageview", { path: loc.pathname });
  }, [loc.pathname]);
  return null;
}

// Once React Router mounts at a `/<path>/index.html` URL (used by share
// buttons so social crawlers hit the prerendered HTML directly), silently
// rewrite the URL bar to the clean canonical form. Real users never see the
// `.html` suffix.
function StripIndexHtml() {
  const loc = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (loc.pathname.endsWith("/index.html")) {
      const clean = loc.pathname.replace(/\/index\.html$/, "") || "/";
      navigate(clean + loc.search + loc.hash, { replace: true });
    }
  }, [loc.pathname, loc.search, loc.hash, navigate]);
  return null;
}

// Emergent Google Auth lands the user back at `<origin>/admin#session_id=<id>`.
// Detect the fragment synchronously (before any route renders) so the one-time
// session exchange always runs first, regardless of which path it lands on.
function AppRoutes({ children }) {
  const loc = useLocation();
  if (loc.hash && loc.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function App() {
  // Strip platform-injected "A product of emergent.sh" meta description so
  // search engines don't pick it up. The Emergent platform script may inject
  // it asynchronously after our load — observe head for re-injection.
  useEffect(() => {
    const stripEmergent = () => {
      document
        .querySelectorAll('meta[name="description"]')
        .forEach((m) => {
          const c = (m.getAttribute("content") || "").toLowerCase();
          if (c.includes("emergent.sh")) {
            m.parentNode && m.parentNode.removeChild(m);
          }
        });
    };
    stripEmergent();
    const obs = new MutationObserver(stripEmergent);
    obs.observe(document.head, { childList: true, subtree: true });
    const t = setTimeout(() => obs.disconnect(), 5000);
    return () => {
      obs.disconnect();
      clearTimeout(t);
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <StripIndexHtml />
          <RouteTracker />
          <MyStackBar />
          <WorkspaceLauncher />
          <AppRoutes>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/index.html" element={<Home />} />
            <Route path="/category/:slug" element={<Home />} />
            <Route path="/c/:asset" element={<Home />} />
            <Route path="/c/:asset/index.html" element={<Home />} />
            <Route path="/c/:asset/:fn" element={<Home />} />
            <Route path="/c/:asset/:fn/index.html" element={<Home />} />
            <Route path="/f/:fn" element={<Home />} />
            <Route path="/f/:fn/index.html" element={<Home />} />
            <Route path="/t/:ticker" element={<Home />} />
            <Route path="/t/:ticker/index.html" element={<Home />} />
            <Route path="/tag/:tag" element={<Home />} />
            <Route path="/tag/:tag/index.html" element={<Home />} />
            <Route path="/sector/:sector" element={<Home />} />
            <Route path="/sector/:sector/index.html" element={<Home />} />
            <Route path="/for/:userbase" element={<Home />} />
            <Route path="/for/:userbase/index.html" element={<Home />} />
            <Route path="/tickers" element={<TickerIndex />} />
            <Route path="/tickers/index.html" element={<TickerIndex />} />
            <Route path="/asset/:slug" element={<Home />} />
            <Route path="/tool/:slug" element={<ToolDetail />} />
            <Route path="/tool/:slug/index.html" element={<ToolDetail />} />
            <Route path="/alternatives/:slug" element={<Alternatives />} />
            <Route path="/alternatives/:slug/index.html" element={<Alternatives />} />
            <Route path="/vs/:pair" element={<Compare />} />
            <Route path="/vs/:pair/index.html" element={<Compare />} />
            <Route path="/stack/:id" element={<Stack />} />
            <Route path="/terminal/:id" element={<Terminal />} />
            {/* Legacy PWA launch target. The manifest start_url is now the
                home index ("/?source=pwa"), but installs created while the
                start_url was "/app?source=pwa" still launch here — bounce them
                to the public home page so the PWA always opens the index. */}
            <Route path="/app" element={<Navigate to="/?source=pwa" replace />} />
            <Route path="/install" element={<Install />} />
            <Route path="/badge" element={<Badge />} />
            <Route path="/best" element={<BestIndex />} />
            <Route path="/best/index.html" element={<BestIndex />} />
            <Route path="/best/:slug" element={<Best />} />
            <Route path="/best/:slug/index.html" element={<Best />} />
            <Route path="/submit" element={<Submit />} />
            <Route path="/claim/:slug" element={<Claim />} />
            <Route path="/vendor" element={<Vendor />} />
            <Route path="/for-vendors" element={<ForVendors />} />
            <Route path="/for-vendors/index.html" element={<ForVendors />} />
            <Route path="/payment/success" element={<PaymentResult outcome="success" />} />
            <Route path="/payment/cancel" element={<PaymentResult outcome="cancel" />} />
            <Route path="/submit/index.html" element={<Submit />} />
            <Route path="/about" element={<About />} />
            <Route path="/about/index.html" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/terms/index.html" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy/index.html" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/sitemap/index.html" element={<Sitemap />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/inbox" element={<AdminInbox />} />
            <Route path="/ask" element={<Navigate to="/" replace />} />
            <Route path="/ask/index.html" element={<Navigate to="/" replace />} />
            <Route path="/build" element={<Build />} />
            <Route path="/workspace" element={<Workspace />} />
            <Route path="/open-source" element={<Home openSource />} />
            <Route path="/open-source/index.html" element={<Home openSource />} />
            <Route path="/list/:id" element={<ListView />} />
          </Routes>
          </AppRoutes>
          <CommandPalette />
        </BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              fontFamily: "'IBM Plex Mono', monospace",
              borderRadius: 0,
              border: "1px solid #000",
              background: "#fff",
              color: "#000",
              fontSize: 13,
            },
          }}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
